import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// 🎨 Loading Skeleton Component
const BlogSkeleton = () => (
    <div className="col-lg-6">
        <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: "16px", overflow: "hidden" }}>
            <div className="skeleton-shimmer" style={{ height: "240px", backgroundColor: "#f0f0f0" }}></div>
            <div className="card-body p-4">
                <div className="skeleton-text mb-3" style={{ height: "28px", width: "80%" }}></div>
                <div className="skeleton-text mb-2" style={{ height: "16px", width: "100%" }}></div>
                <div className="skeleton-text mb-2" style={{ height: "16px", width: "95%" }}></div>
                <div className="skeleton-text mb-3" style={{ height: "16px", width: "60%" }}></div>
                <div className="skeleton-button" style={{ height: "38px", width: "100px", borderRadius: "20px" }}></div>
            </div>
        </div>
    </div>
);

// 📄 Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }) => {
    const getPageNumbers = () => {
        const delta = 2;
        const range = [];
        const rangeWithDots = [];
        let l;

        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                range.push(i);
            }
        }

        range.forEach((i) => {
            if (l) {
                if (i - l === 2) {
                    rangeWithDots.push(l + 1);
                } else if (i - l !== 1) {
                    rangeWithDots.push('...');
                }
            }
            rangeWithDots.push(i);
            l = i;
        });

        return rangeWithDots;
    };

    if (totalPages <= 1) return null;

    return (
        <div className="d-flex justify-content-center mt-5">
            <nav aria-label="Blog pagination">
                <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{ color: "#0a1f44" }}
                        >
                            <i className="fa fa-chevron-left"></i> Previous
                        </button>
                    </li>

                    {getPageNumbers().map((page, index) => (
                        <li key={index} className={`page-item ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}>
                            {page === '...' ? (
                                <span className="page-link">...</span>
                            ) : (
                                <button
                                    className="page-link"
                                    onClick={() => onPageChange(page)}
                                    style={{
                                        backgroundColor: page === currentPage ? '#ff8c00' : 'transparent',
                                        borderColor: page === currentPage ? '#ff8c00' : '#dee2e6',
                                        color: page === currentPage ? 'white' : '#0a1f44'
                                    }}
                                >
                                    {page}
                                </button>
                            )}
                        </li>
                    ))}

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{ color: "#0a1f44" }}
                        >
                            Next <i className="fa fa-chevron-right"></i>
                        </button>
                    </li>
                </ul>
            </nav>
        </div>
    );
};

function Blog() {
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 📄 Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalBlogs, setTotalBlogs] = useState(0);
    const [itemsPerPage] = useState(6); // 6 blogs per page (2 columns x 3 rows)

    // Add shimmer styles
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .skeleton-shimmer {
                background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                background-size: 200% 100%;
                animation: shimmer 1.5s infinite;
            }
            
            .skeleton-text {
                background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                background-size: 200% 100%;
                animation: shimmer 1.5s infinite;
                border-radius: 4px;
            }
            
            .skeleton-button {
                background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                background-size: 200% 100%;
                animation: shimmer 1.5s infinite;
                border-radius: 20px;
            }
            
            @keyframes shimmer {
                0% { background-position: 200% 0; }
                100% { background-position: -200% 0; }
            }
            
            .pagination .page-item.active .page-link {
                background-color: #ff8c00;
                border-color: #ff8c00;
            }
            
            .pagination .page-link {
                color: #0a1f44;
                cursor: pointer;
            }
            
            .pagination .page-item.disabled .page-link {
                color: #6c757d;
                cursor: not-allowed;
            }
            
            .pagination .page-link:hover:not(.disabled) {
                background-color: #ff8c00;
                color: white;
                border-color: #ff8c00;
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    // 🔥 fetch blogs from backend with pagination
    const fetchBlogs = async (page = 1) => {
        try {
            setLoading(true);
            setError(null);

            const url = `${process.env.REACT_APP_API_URL}/blogs?page=${page}&limit=${itemsPerPage}`;
            console.log(`📡 Fetching blogs: ${url}`);

            const res = await fetch(url);

            if (!res.ok) {
                throw new Error(`HTTP ${res.status}: ${res.statusText}`);
            }

            const data = await res.json();

            // Check if response has pagination structure
            if (data.pagination) {
                // New paginated response format
                setBlogPosts(data.data);
                setCurrentPage(data.pagination.page);
                setTotalPages(data.pagination.totalPages);
                setTotalBlogs(data.pagination.total);
            } else if (Array.isArray(data)) {
                // Old format - no pagination (fallback)
                setBlogPosts(data);
                setTotalPages(1);
                setCurrentPage(1);
                setTotalBlogs(data.length);
            } else {
                throw new Error("Invalid response format");
            }

        } catch (err) {
            console.error("Failed to fetch blogs:", err);
            setError(err.message);

            // Fallback to non-paginated endpoint
            try {
                console.log("🔄 Falling back to non-paginated endpoint...");
                const fallbackRes = await fetch(`${process.env.REACT_APP_API_URL}/blogs`);
                if (fallbackRes.ok) {
                    const fallbackData = await fallbackRes.json();
                    if (Array.isArray(fallbackData)) {
                        setBlogPosts(fallbackData);
                        setTotalPages(1);
                        setCurrentPage(1);
                        setTotalBlogs(fallbackData.length);
                    }
                }
            } catch (fallbackErr) {
                console.error("Fallback also failed:", fallbackErr);
            }

        } finally {
            setLoading(false);
        }
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            fetchBlogs(newPage);
            // Scroll to top when page changes
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Handle retry on error
    const handleRetry = () => {
        fetchBlogs(currentPage);
    };

    useEffect(() => {
        fetchBlogs(1);
    }, []);

    return (
        <div style={{ backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
            {/* Hero */}
            <div className="container-fluid py-5 mb-5 navyblue-bg hero-header">
                <div className="container py-5 text-center">
                    <h1 className="display-3 fw-bold text-white">
                        PrimeDale Insights
                    </h1>
                    <p className="text-warning fs-5 mt-3">
                        Corporate Travel • Team Building • Executive Retreats
                    </p>
                    {totalBlogs > 0 && !loading && (
                        <p className="text-white mt-3">
                            <i className="fas fa-newspaper me-2"></i>
                            {totalBlogs} articles to inspire your next corporate experience
                        </p>
                    )}
                </div>
            </div>

            {/* Intro */}
            <div className="container py-5 text-center">
                <p className="lead text-secondary">
                    Welcome to the PrimeDale Blog — your trusted source for expert
                    insights, industry trends, and practical guidance in corporate
                    travel, executive retreats, and team-building experiences.
                </p>
            </div>

            {/* Blogs */}
            <div className="container pb-5">
                {/* Error State */}
                {error && (
                    <div className="alert alert-danger text-center" role="alert">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {error}
                        <button
                            className="btn btn-sm btn-outline-danger ms-3"
                            onClick={handleRetry}
                        >
                            <i className="fas fa-sync-alt me-1"></i>
                            Retry
                        </button>
                    </div>
                )}

                {/* Loading Skeletons */}
                {loading && (
                    <div className="row g-4">
                        {[...Array(6)].map((_, i) => (
                            <BlogSkeleton key={i} />
                        ))}
                    </div>
                )}

                {/* Blog Posts Grid */}
                {!loading && !error && blogPosts.length > 0 && (
                    <>
                        <div className="row g-4">
                            {blogPosts.map((blog) => (
                                <div className="col-lg-6" key={blog.id}>
                                    <div
                                        className="card h-100 border-0 shadow-sm"
                                        style={{
                                            borderRadius: "16px",
                                            overflow: "hidden",
                                            transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = "translateY(-5px)";
                                            e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.1)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = "translateY(0)";
                                            e.currentTarget.style.boxShadow = "none";
                                        }}
                                    >
                                        <img
                                            src={blog.image || "/assets/img/placeholder.jpg"}
                                            alt={blog.title}
                                            className="blog-image"
                                            loading="lazy"


                                        />

                                        <div className="card-body p-4">
                                            <h4
                                                className="fw-bold mb-3"
                                                style={{ color: "#0a1f44" }}
                                            >
                                                {blog.title}
                                            </h4>

                                            <p className="text-secondary">
                                                {blog.intro?.length > 150
                                                    ? `${blog.intro.substring(0, 150)}...`
                                                    : blog.intro}
                                            </p>

                                            {/* author + date */}
                                            {(blog.author || blog.date) && (
                                                <p className="small text-muted mb-2">
                                                    {blog.author && (
                                                        <>
                                                            <i className="fas fa-user me-1"></i>
                                                            By {blog.author}
                                                        </>
                                                    )}
                                                    {blog.author && blog.date && " • "}
                                                    {blog.date && (
                                                        <>
                                                            <i className="fas fa-calendar-alt me-1"></i>
                                                            {new Date(blog.date).toLocaleDateString()}
                                                        </>
                                                    )}
                                                </p>
                                            )}
                                            <style>{`
                                                .blog-image {
                                                width: 100%;
                                                
                                                object-fit: fit;
                                                
                                                border-radius: 8px;
                                            }
                                           
                                            /* Tablets */
                                            @media (max-width: 768px) {
                                                .blog-image {
                                                    aspect-ratio: 16 / 10;
                                                }
                                            }

                                            /* Phones */
                                            @media (max-width: 576px) {
                                                .blog-image {
                                                    aspect-ratio: 4 / 3;
                                                }
                                            }
                                             
                                           `}</style>
                                            <div className="mt-3">
                                                <Link
                                                    to={`/blogs/${blog.slug}`}
                                                    className="btn btn-sm me-2 mb-2"
                                                    style={{
                                                        border: "1px solid #0a1f44",
                                                        color: "#0a1f44",
                                                        borderRadius: "20px",
                                                        transition: "all 0.3s ease",
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.backgroundColor = "#0a1f44";
                                                        e.target.style.color = "white";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.backgroundColor = "transparent";
                                                        e.target.style.color = "#0a1f44";
                                                    }}
                                                >
                                                    Read More <i className="fas fa-arrow-right ms-1"></i>
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination Component */}
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            totalItems={totalBlogs}
                            itemsPerPage={itemsPerPage}
                        />

                        {/* Showing Info */}
                        {totalBlogs > 0 && (
                            <div className="text-center text-muted mt-3">
                                <small>
                                    Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                                    {Math.min(currentPage * itemsPerPage, totalBlogs)} of {totalBlogs} articles
                                </small>
                            </div>
                        )}
                    </>
                )}

                {/* No Blogs Found */}
                {!loading && !error && blogPosts.length === 0 && (
                    <div className="text-center py-5">
                        <i className="fas fa-newspaper fa-3x text-muted mb-3"></i>
                        <h4>No blog posts found</h4>
                        <p>Check back soon for new insights and articles!</p>
                        <button
                            className="btn btn-primary mt-3"
                            onClick={handleRetry}
                            style={{ borderRadius: "25px", backgroundColor: "#0a1f44" }}
                        >
                            <i className="fas fa-sync-alt me-2"></i>
                            Refresh
                        </button>
                    </div>
                )}
            </div>

            {/* CTA */}
            <div
                className="container-fluid py-5 text-center"
                style={{ backgroundColor: "#0a1f44" }}
            >
                <h3 className="fw-bold text-white mb-3">
                    Plan Your Next Corporate Experience
                </h3>
                <p className="text-light mb-4">
                    Let PrimeDale handle your corporate travel, retreats, and incentive
                    programs — professionally and seamlessly.
                </p>
                <a
                    href="/contact"
                    className="btn me-3 px-4 py-2"
                    style={{
                        backgroundColor: "#ff8c00",
                        color: "#fff",
                        borderRadius: "25px",
                        transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#e67e00";
                        e.target.style.transform = "scale(1.05)";
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#ff8c00";
                        e.target.style.transform = "scale(1)";
                    }}
                >
                    Contact Us
                </a>
                <a
                    href="/newsletter"
                    className="btn px-4 py-2"
                    style={{
                        border: "2px solid #ff8c00",
                        color: "#ff8c00",
                        borderRadius: "25px",
                        transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#ff8c00";
                        e.target.style.color = "white";
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        e.target.style.color = "#ff8c00";
                    }}
                >
                    Subscribe
                </a>
            </div>
        </div>
    );
}

export default Blog;